import { useCallback, useMemo } from "react";

import TransferValueModalView, {
  calculatePosition,
  calculateScaleBoundaries,
  getIndicatorColors,
} from "./TransferValueModalView";

import {
  CurrencyCode,
  PlayerDetailPlayerMetaFragment,
} from "@/generated/graphql";
import { useTheme } from "@/providers/ThemeProvider";
import formatCurrencyShort from "@/utils/formatCurrencyShort";
import {
  alignPublicPosition,
  resolveOverlappingPositions,
} from "@/utils/valuationPositionUtils";

/**
 * Props for the TransferValueModalContainer component
 */
interface TransferValueModalContainerProps {
  isOpen: boolean;
  onClose: () => void;
  player: PlayerDetailPlayerMetaFragment;
  currencyCode: CurrencyCode;
}

/**
 * Container component for transfer value modal with fee calculations and assessments.
 * Uses same fallback chain as extractIndicatorData: prefers new Gemini valuation fields,
 * falls back to deprecated fairFee/expectedFee for backward compatibility.
 * @param root0 - Component props
 * @param root0.isOpen - Whether modal is currently visible
 * @param root0.onClose - Callback invoked when modal should close
 * @param root0.player - Player object containing transfer value data
 * @param root0.currencyCode - Currency code for formatting monetary values
 */
const TransferValueModalContainer = ({
  isOpen,
  onClose,
  player,
  currencyCode,
}: TransferValueModalContainerProps) => {
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const { resolvedTheme } = useTheme();
  const isDark = useMemo(() => resolvedTheme === "dark", [resolvedTheme]);

  const { bioData } = player;

  // Use same fallback chain as extractIndicatorData: prefer new fields, fall back to deprecated.
  // Treat 0 as null since a €0 valuation is meaningless (SE-3837).
  const geminiMin =
    bioData?.minGeminiPlayerValuation ?? bioData?.fairFee ?? null;
  const geminiMax =
    bioData?.maxGeminiPlayerValuation ?? bioData?.expectedFee ?? null;
  const playerValuation = bioData?.playerValuation;
  const validGeminiMin = geminiMin && geminiMin > 0 ? geminiMin : null;
  const validGeminiMax = geminiMax && geminiMax > 0 ? geminiMax : null;

  // Calculate scale boundaries and positions for markers
  const { scaleMin, scaleMax } = useMemo(
    () =>
      calculateScaleBoundaries([
        validGeminiMin,
        validGeminiMax,
        playerValuation,
      ]),
    [validGeminiMin, validGeminiMax, playerValuation]
  );
  const minPosition = useMemo(
    () => calculatePosition(validGeminiMin, scaleMin, scaleMax),
    [validGeminiMin, scaleMin, scaleMax]
  );
  const maxPosition = useMemo(
    () => calculatePosition(validGeminiMax, scaleMin, scaleMax),
    [validGeminiMax, scaleMin, scaleMax]
  );
  const publicPosition = useMemo(
    () => calculatePosition(playerValuation, scaleMin, scaleMax),
    [playerValuation, scaleMin, scaleMax]
  );

  // Resolve overlapping label positions for narrow ranges
  const { adjustedMinPos, adjustedMaxPos } = useMemo(
    () => resolveOverlappingPositions(minPosition, maxPosition),
    [minPosition, maxPosition]
  );

  // Align public position to bound position when their display labels match
  const alignedPublicPosition = useMemo(
    () =>
      alignPublicPosition(
        publicPosition,
        minPosition,
        maxPosition,
        playerValuation,
        validGeminiMin,
        validGeminiMax,
        (v: number) => formatCurrencyShort(v, currencyCode)
      ),
    [
      publicPosition,
      minPosition,
      maxPosition,
      playerValuation,
      validGeminiMin,
      validGeminiMax,
      currencyCode,
    ]
  );

  // Get theme-appropriate colors
  const { greenColor, orangeColor, blueColor } = useMemo(
    () => getIndicatorColors(isDark),
    [isDark]
  );

  return (
    <TransferValueModalView
      isOpen={isOpen}
      onClose={handleClose}
      playerName={player?.name ?? ""}
      currencyCode={currencyCode}
      isDark={isDark}
      geminiMin={validGeminiMin}
      geminiMax={validGeminiMax}
      playerValuation={playerValuation}
      minPosition={minPosition}
      maxPosition={maxPosition}
      adjustedMinPosition={adjustedMinPos}
      adjustedMaxPosition={adjustedMaxPos}
      publicPosition={alignedPublicPosition}
      greenColor={greenColor}
      orangeColor={orangeColor}
      blueColor={blueColor}
    />
  );
};

export default TransferValueModalContainer;
